import { Renew, Warning, Wifi } from '@carbon/icons-react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import { type ReactNode, useEffect, useState } from 'react';

import { useInternetSpeedCheck } from '../../hooks/use-internet-speed-check';

export const InternetSpeedTester = () => {
  const {
    downloadStatus,
    uploadStatus,
    downloadSpeedMbps,
    uploadSpeedMbps,
    isDownloadTooSlow,
    isUploadTooSlow,
    isTesting,
    verdict,
    runSpeedTest,
  } = useInternetSpeedCheck();

  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    // Auto-expand if the connection is not good, but only after the test is complete.
    if (!isTesting && verdict !== 'Good') {
      setIsExpanded(true);
    }
  }, [isTesting, verdict]);

  const renderTestResult = (
    status: string,
    speedMbps: number | null,
    isTooSlow: boolean,
    type: 'Download' | 'Upload',
  ) => {
    let content: ReactNode = null;

    switch (status) {
      case 'testing':
        content = (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant='body2' color='text.secondary'>
              Testing...
            </Typography>
          </Box>
        );
        break;
      case 'completed':
        content = (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isTooSlow ? <Warning color='#ef4444' /> : <Wifi color='#22c55e' />}
            <Typography
              variant='body2'
              sx={{
                fontWeight: 'medium',
                fontFamily: 'monospace',
                fontSize: '0.9rem',
              }}
              color={isTooSlow ? 'error' : 'inherit'}
            >
              {speedMbps?.toFixed(1)} Mbps
            </Typography>
          </Box>
        );
        break;
      case 'error':
        content = (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning color='#ef4444' />
            <Typography variant='body2' color='error'>
              Test failed
            </Typography>
          </Box>
        );
        break;
      default:
        content = null;
    }

    return (
      <Grid container item xs={12} alignItems='center'>
        <Grid item xs={6}>
          <Typography variant='body2'>{type} Speed</Typography>
        </Grid>
        <Grid item xs={6}>
          {content}
        </Grid>
      </Grid>
    );
  };

  const renderVerdictChip = () => {
    if (isTesting) return null;

    switch (verdict) {
      case 'Good':
        return <Chip label='Good' color='success' size='small' />;
      case 'Fair':
        return <Chip label='Fair' color='warning' size='small' />;
      case 'Poor':
        return <Chip label='Poor' color='error' size='small' />;
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: 'action.hover',
        p: 2,
        borderRadius: 1,
        my: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant='subtitle2'>Connection Test</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {renderVerdictChip()}
          <Tooltip title='Retest connection'>
            <IconButton
              onClick={runSpeedTest}
              size='small'
              disabled={isTesting}
            >
              <Renew />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {isTesting && <LinearProgress sx={{ my: 1.5 }} />}

      <Collapse in={isExpanded && !isTesting}>
        <Grid container spacing={1} sx={{ mt: 1 }}>
          {renderTestResult(
            downloadStatus,
            downloadSpeedMbps,
            isDownloadTooSlow,
            'Download',
          )}
          {renderTestResult(
            uploadStatus,
            uploadSpeedMbps,
            isUploadTooSlow,
            'Upload',
          )}
        </Grid>
        {verdict !== 'Good' && (
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ mt: 1.5, display: 'block' }}
          >
            For the best experience, we recommend a connection of at least 4
            Mbps for both download and upload.
          </Typography>
        )}
      </Collapse>

      {!isTesting && !isExpanded && verdict === 'Good' && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 1,
            mt: 1,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant='body2' color='text.secondary'>
            Your internet connection is looking good.
          </Typography>
          <Button
            onClick={() => setIsExpanded(true)}
            size='small'
            sx={{ left: '-4px' }}
            variant='text'
          >
            Show details
          </Button>
        </Box>
      )}
    </Box>
  );
};
